/* ==================== Enquiries.jsx - FULL UPDATED VERSION ==================== */
import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faEdit,
  faTrash,
  faQrcode,
} from "@fortawesome/free-solid-svg-icons";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "bootstrap/dist/css/bootstrap.min.css";
import "../Enquiries/Enquiries.css";
import AddEnquiry from "../AddEnquiry/AddEnquiry";

// Clean URL – no query param needed anymore (handled by routing / isPublic logic)
const PUBLIC_FORM_URL = "https://aadyaacademy.org/add-enquiry";
// Alternative (works in dev & prod): const PUBLIC_FORM_URL = `${window.location.origin}/add-enquiry`;

const API_BASE_URL = "https://aadyaacademy.org/api";

function Enquiries() {
  const [enquiryList, setEnquiryList] = useState([]);
  const [filteredEnquiry, setFilteredEnquiry] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingEnquiry, setEditingEnquiry] = useState(null);
  const [showQRModal, setShowQRModal] = useState(false);

  // Fetch enquiries + filter out admitted/withdrawn/cancelled
  const fetchEnquiries = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/enquiries/list`);
      if (!response.ok) throw new Error("Failed to fetch enquiries");
      const data = await response.json();
      const rawData = Array.isArray(data) ? data : (data.data || []);
      const activeEnquiries = rawData.filter((e) => {
        const status = (e.enquiry_status || "").trim();
        return status !== "Admitted" && status !== "Withdrawn" && status !== "Cancelled";
      });
      const enriched = activeEnquiries.map((e) => ({
        ...e,
        enquiry_id: e.enquiry_id || e.id,
        phone: e.father_phone || e.mother_phone || "N/A",
        dob: e.dob ? new Date(e.dob).toISOString().split("T")[0] : "",
        enquiry_date: e.created_at ? new Date(e.created_at).toISOString().split("T")[0] : "",
        remarks: e.remarks || "",
      }));
      setEnquiryList(enriched);
      setFilteredEnquiry(enriched);
    } catch (err) {
      console.error("Fetch enquiries error:", err);
      toast.error("Failed to load enquiries");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEnquiries();
  }, []);

  // Search filter
  useEffect(() => {
    let filtered = [...enquiryList];
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (e) =>
          String(e.enquiry_id || "").includes(term) ||
          (e.enquiry_name || "").toLowerCase().includes(term) ||
          (e.phone || "").includes(term) ||
          String(e.class || "").toLowerCase().includes(term)
      );
    }
    setFilteredEnquiry(filtered);
  }, [searchTerm, enquiryList]);

  const formatDateToDisplay = (date) => {
    if (!date) return "N/A";
    const [y, m, d] = date.split("-");
    return `${d}/${m}/${y}`;
  };

  const startEdit = (enquiry) => {
    setEditingEnquiry(enquiry);
    setShowAddForm(true);
  };

  const handleDelete = async (enquiry) => {
    if (!window.confirm(`Delete "${enquiry.enquiry_name}"?`)) return;
    try {
      const response = await fetch(`${API_BASE_URL}/enquiries/delete/${enquiry.enquiry_id}`, {
        method: "DELETE",
      });
      if (response.ok) {
        toast.success("Deleted successfully");
        fetchEnquiries();
      } else {
        toast.error("Delete failed");
      }
    } catch (err) {
      toast.error("Connection error");
    }
  };

  const handleRemarksChange = async (enquiry, value) => {
    let status = "Pending";
    if (value === "Admission Taken") status = "Admitted";
    if (value === "Not Interested") status = "Withdrawn";
    try {
      const response = await fetch(`${API_BASE_URL}/enquiries/update/${enquiry.enquiry_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          remarks: value,
          enquiry_status: status,
        }),
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || "Update failed");
      }
      toast.success(
        value === "Admission Taken"
          ? "Moved to Admissions list!"
          : `Updated to: ${value}`
      );
      fetchEnquiries();
    } catch (err) {
      console.error("Update error:", err);
      toast.error("Update failed: " + err.message);
    }
  };

  const handleCancel = () => {
    setShowAddForm(false);
    setEditingEnquiry(null);
  };

  if (showAddForm) {
    return (
      <AddEnquiry
        editingEnquiry={editingEnquiry}
        fetchData={fetchEnquiries}
        handleCancel={handleCancel}
      />
    );
  }

  return (
    <div className="enquiries">
      <div className="enquiries-table">
        <div className="table-header">
          <div className="table-header-top">
            <h2>Enquiries List: {filteredEnquiry.length}</h2>
          </div>
          <div className="table-header-btm">
            <input
              type="text"
              className="search-input"
              placeholder="Search by ID, Name, Phone, Class..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button className="add-enquiry-btn" onClick={() => setShowAddForm(true)}>
              <FontAwesomeIcon icon={faPlus} /> Add New
            </button>
            <button
              className="add-enquiry-btn qr-btn"
              onClick={() => setShowQRModal(true)}
            >
              <FontAwesomeIcon icon={faQrcode} /> QR for Parents
            </button>
          </div>
        </div>

        <div className="table-content">
          <table>
            <thead>
              <tr>
                <th>S.No</th>
                <th>Name</th>
                <th>Class</th>
                <th>DOB</th>
                <th>Phone</th>
                <th>Remarks</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" style={{ textAlign: "center" }}>Loading...</td>
                </tr>
              ) : filteredEnquiry.length === 0 ? (
                <tr>
                  <td colSpan="7" style={{ textAlign: "center" }}>
                    No pending enquiries
                  </td>
                </tr>
              ) : (
                filteredEnquiry.map((enquiry, index) => (
                  <tr key={enquiry.enquiry_id}>
                    <td>{index + 1}</td>
                    <td>{enquiry.enquiry_name}</td>
                    <td>{enquiry.class || "-"}</td>
                    <td>{formatDateToDisplay(enquiry.dob)}</td>
                    <td>{enquiry.phone || "-"}</td>
                    <td>
                      <select
                        value={enquiry.remarks || ""}
                        onChange={(e) => handleRemarksChange(enquiry, e.target.value)}
                        className="remarks-dropdown"
                      >
                        <option value="">Select</option>
                        <option value="Followup">Followup</option>
                        <option value="Not Interested">Not Interested</option>
                        <option value="Admission Taken">Admission Taken</option>
                      </select>
                    </td>
                    <td className="actions">
                      <button
                        onClick={() => startEdit(enquiry)}
                        className="action-btn edit-btn"
                        title="Edit"
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                      <button
                        onClick={() => handleDelete(enquiry)}
                        className="action-btn delete-btn"
                        title="Delete"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showQRModal && (
        <div className="qr-modal-overlay" onClick={() => setShowQRModal(false)}>
          <div className="qr-modal" onClick={(e) => e.stopPropagation()}>
            <button className="qr-close-btn" onClick={() => setShowQRModal(false)}>
              ×
            </button>
            <h3>Parent Enquiry Form</h3>
            <p style={{ color: "#555", margin: "15px 0" }}>
              Scan to open the enquiry form directly on your phone (no login needed)
            </p>
            <img
              src={`https://api.qrserver.com/v1/create-qr-code/?size=500x500&data=${encodeURIComponent(PUBLIC_FORM_URL)}&color=9F7AEA&bgcolor=FFFFFF&qzone=4`}
              alt="Parent QR Code"
              style={{
                width: "300px",
                height: "300px",
                padding: "20px",
                background: "linear-gradient(135deg, #fdb8d8, #d0b8ff)",
                borderRadius: "30px",
                border: "10px solid white",
                boxShadow: "0 20px 40px rgba(159,122,234,0.5)",
              }}
            />
            <p style={{ marginTop: "20px", fontWeight: "bold", color: "#6b46c1", fontSize: "18px" }}>
              Scan with Phone Camera
            </p>
          </div>
        </div>
      )}

      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
}

export default Enquiries;